import React, { useState } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Badge } from "react-bootstrap";
import { FaMapMarkerAlt, FaEnvelope, FaClock, FaUsers, FaHandshake } from "react-icons/fa";
import communityImage from "../assets/community.jpg";

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send message");
      }
    } catch (err) {
      setError(err.message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section 
        className="py-5 d-flex align-items-center position-relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 193, 7, 0.85), rgba(255, 152, 0, 0.9)), url(${communityImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "60vh",
          marginTop: "-80px",
          paddingTop: "80px"
        }}
      >
        <Container className="text-center text-white">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Badge bg="light" text="dark" className="mb-3 fs-6 px-3 py-2">💬 GET IN TOUCH</Badge>
              <h1 className="fw-bold display-4 mb-4">Contact Mbaitu USA</h1>
              <p className="lead fs-5 mb-4">
                Ready to join our community or have questions? We're here to help! 
                Reach out and let's start the conversation.
              </p>
              <div className="d-flex justify-content-center gap-4 flex-wrap">
                <div className="text-center">
                  <FaUsers size={24} className="mb-2" />
                  <div className="fw-bold">45+ Members</div>
                </div>
                <div className="text-center">
                  <FaHandshake size={24} className="mb-2" />
                  <div className="fw-bold">5+ Years</div>
                </div>
                <div className="text-center">
                  <FaMapMarkerAlt size={24} className="mb-2" />
                  <div className="fw-bold">Texas Based</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="py-5">
        <Row className="g-4">
          {/* Contact Information Cards */}
          <Col lg={4}>
            <Card className="h-100 shadow-sm border-0 p-4 text-center card-lift">
              <div className="mb-3">
                <div className="p-3 bg-primary bg-opacity-10 rounded-circle d-inline-block">
                  <FaMapMarkerAlt className="text-primary" size={32} />
                </div>
              </div>
              <h5 className="fw-bold">Our Location</h5>
              <p className="text-muted">
                <strong>Dallas-Fort Worth Area</strong><br />
                Texas, United States<br />
                <small className="text-muted">Serving all of Texas</small>
              </p>
            </Card>
          </Col>
          
          <Col lg={4}>
            <Card className="h-100 shadow-sm border-0 p-4 text-center card-lift">
              <div className="mb-3">
                <div className="p-3 bg-success bg-opacity-10 rounded-circle d-inline-block">
                  <FaEnvelope className="text-success" size={32} />
                </div>
              </div>
              <h5 className="fw-bold">Email Us</h5>
              <p className="text-muted">
                <a href="mailto:info@mbaituusa.org" className="text-decoration-none">
                  info@mbaituusa.org
                </a><br />
                <small className="text-muted">We respond within 24 hours</small>
              </p>
            </Card>
          </Col>
          
          <Col lg={4}>
            <Card className="h-100 shadow-sm border-0 p-4 text-center card-lift">
              <div className="mb-3">
                <div className="p-3 bg-warning bg-opacity-10 rounded-circle d-inline-block">
                  <FaClock className="text-warning" size={32} />
                </div>
              </div>
              <h5 className="fw-bold">Meeting Times</h5>
              <p className="text-muted">
                Quarterly Meetings<br />
                <strong>Check Events page for dates</strong><br />
                <small className="text-muted">See Events for details</small>
              </p>
            </Card>
          </Col>
        </Row>
        
        <Row className="mt-5">
          {/* Enhanced Contact Form */}
          <Col lg={8} className="mx-auto">
            <Card className="shadow-sm border-0 p-5">
              <div className="text-center mb-4">
                <h3 className="fw-bold">✉️ Send Us a Message</h3>
                <p className="text-muted">
                  Whether you're interested in joining, have questions about our programs, 
                  or want to get involved, we'd love to hear from you.
                </p>
              </div>

              {submitted && (
                <Alert variant="success" className="d-flex align-items-center">
                  <FaEnvelope className="me-2" />
                  ✅ Your message has been sent! We'll get back to you within 24 hours.
                </Alert>
              )}
              
              {error && (
                <Alert variant="danger" className="d-flex align-items-center">
                  <FaEnvelope className="me-2" />
                  ❌ {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="p-3"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="p-3"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">Message *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your interest in Mbaitu USA, questions you have, or how you'd like to get involved..."
                    className="p-3"
                    required
                  />
                </Form.Group>

                <div className="text-center">
                  <Button 
                    variant="warning" 
                    type="submit" 
                    size="lg"
                    className="px-5 fw-bold shadow"
                    disabled={!formData.name || !formData.email || !formData.message || loading}
                  >
                    {loading ? "📤 Sending..." : "📨 Send Message"}
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
        
        {/* Additional Information */}
        <Row className="mt-5">
          <Col lg={10} className="mx-auto">
            <Card className="border-0 bg-light p-4">
              <h5 className="fw-bold text-center mb-3">🤝 Ways to Connect</h5>
              <Row className="text-center g-4">
                <Col md={3}>
                  <div className="h-100">
                    <FaUsers className="text-primary mb-2" size={24} />
                    <h6 className="fw-bold">Join as Member</h6>
                    <small className="text-muted">Become a full community member</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="h-100">
                    <FaClock className="text-success mb-2" size={24} />
                    <h6 className="fw-bold">Attend Events</h6>
                    <small className="text-muted">Join our quarterly meetings</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="h-100">
                    <FaHandshake className="text-warning mb-2" size={24} />
                    <h6 className="fw-bold">Volunteer</h6>
                    <small className="text-muted">Help with community initiatives</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="h-100">
                    <FaEnvelope className="text-info mb-2" size={24} />
                    <h6 className="fw-bold">Stay Updated</h6>
                    <small className="text-muted">Get our community newsletter</small>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Contact;